import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import MethodChip from './MethodChip';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { hexToPastel } from '../../../stylesSheets';

export default function GroupEndpointsTable({ group, overrides, getOverride, onToggleOverride }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const getPastelColor = (colorHex) => {
    try {
      return hexToPastel(colorHex);
    } catch (e) {
      return colorHex;
    }
  };

  const endpoints = group.endpoints || [];

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, border: 'none', mt: 1 }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('cws_app.management.method_header', 'METHOD')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('cws_app.management.path_header', 'PATH')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('cws_app.management.source_header', 'SOURCE')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('cws_app.management.status_header', 'STATUS')}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: 'text.secondary' }}>
              {t('cws_app.management.action_header', 'ACTION')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {endpoints.map((ep) => {
            const override = getOverride(ep);
            const isRevoked = override?.type === 'revoke';
            
            const sourceText = isRevoked
              ? t('cws_app.management.override', 'Override')
              : t('cws_app.management.group', 'Group');
            const statusText = isRevoked
              ? t('cws_app.management.revoked', 'REVOKED')
              : t('cws_app.management.granted', 'GRANTED');
            
            const statusColor = isRevoked ? theme.palette.error.main : theme.palette.success.main;
            const statusPastelBg = getPastelColor(statusColor);
            const statusTextColor = isRevoked ? theme.palette.error.dark : theme.palette.success.dark;

            return (
              <TableRow
                key={`${ep.method}-${ep.path}`}
                sx={{
                  opacity: isRevoked ? 0.7 : 1,
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell sx={{ py: 1 }}>
                  <MethodChip method={ep.method} />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', textDecoration: isRevoked ? 'line-through' : 'none' }}>
                    {ep.path}
                  </Typography>
                  {ep.parameters?.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {ep.parameters.map((p) => (
                        <Chip
                          key={p.name}
                          label={`${p.name} (${p.location})`}
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            backgroundColor: theme.palette.action.selected,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                  {sourceText}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={statusText}
                    size="small"
                    sx={{
                      backgroundColor: statusPastelBg,
                      color: statusTextColor,
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: statusColor,
                      height: 20,
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1.5,
                      borderRadius: '4px',
                      backgroundColor: isRevoked ? theme.palette.primary.main : theme.palette.warning.main,
                      color: isRevoked ? theme.palette.primary.contrastText : theme.palette.warning.contrastText,
                      '&:hover': {
                        backgroundColor: isRevoked ? theme.palette.primary.dark : theme.palette.warning.dark,
                      }
                    }}
                    onClick={() => onToggleOverride(ep, 'revoke')}
                  >
                    {isRevoked
                      ? t('cws_app.management.restore', 'RESTORE')
                      : t('cws_app.management.revoke', 'REVOKE')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
